import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Divider,
  TextField,
  CircularProgress,
  Chip,
  Tab,
  Tabs,
  Tooltip,
  Fade,
  IconButton,
  List as ListIcon,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Slide
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Visibility';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import ScheduleIcon from '@mui/icons-material/Schedule';
import StarIcon from '@mui/icons-material/Star';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoIcon from '@mui/icons-material/Info';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SettingsOverscanIcon from '@mui/icons-material/SettingsOverscan';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import AddIcon from '@mui/icons-material/Add';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import TabletIcon from '@mui/icons-material/Tablet';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import LightbulbIcon from '@mui/icons-material/LightbulbOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import api from '../../../../api/client';
import RichTextEditor from '../../../LexicalRichText';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import DynamicBasicDialog from '../../component/DynamicBasicDialog';
import { useUser } from '../../../../contexts/userContext';

const TEMPLATE_TYPES = [
  { id: 'interview_invite', label: 'Interview Invitation', description: 'Sent to candidates when they are invited for an interview.', icon: <EmailIcon sx={{ fontSize: 20 }} /> },
  { id: 'candidate_rejection', label: 'Candidate Rejection', description: 'Sent to candidates when they are rejected.', icon: <BlockIcon sx={{ fontSize: 20 }} /> },
  { id: 'candidate_selection', label: 'Candidate Selection', description: 'Sent to candidates when they are selected for the next round.', icon: <CheckCircleOutlineIcon sx={{ fontSize: 20 }} /> },
  { id: 'manager_notification', label: 'Manager Notification', description: 'Sent to hiring managers about candidate progress.', icon: <SettingsOverscanIcon sx={{ fontSize: 20 }} /> },
  { id: 'feedback_report', label: 'Feedback Report', description: 'Sent to candidates with their interview performance feedback.', icon: <FactCheckOutlinedIcon sx={{ fontSize: 20 }} /> },
];

const EMAIL_STYLES = [
  { id: 'minimal', label: 'Minimalist Clean', icon: '✨', description: 'Simple logo & clean content', color: '#10b981', bg: '#ecfdf5' },
  { id: 'modern_card', label: 'Modern Professional', icon: '💳', description: 'Gradient header & card layout', color: '#6366f1', bg: '#eef2ff' },
  { id: 'formal', label: 'Executive Formal', icon: '🏛️', description: 'Corporate text & structured sections', color: '#0f172a', bg: '#f1f5f9' },
  { id: 'creative', label: 'Enterprise Structured', icon: '🎨', description: 'Info cards & strong CTA', color: '#f59e0b', bg: '#fffbeb' },
  { id: 'compact', label: 'Compact Direct', icon: '💬', description: 'Plain text style & inline link', color: '#ec4899', bg: '#fdf2f8' },
];

const AVAILABLE_VARIABLES = [
  {
    category: 'Candidate',
    icon: <PersonIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
    variables: ['{{ candidate_name }}', '{{ candidate_email }}'],
    appliesTo: ['interview_invite', 'candidate_rejection', 'candidate_selection', 'manager_notification', 'feedback_report'],
  },
  {
    category: 'Job',
    icon: <WorkIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
    variables: ['{{ job_title }}', '{{ company_name }}'],
    appliesTo: ['interview_invite', 'candidate_rejection', 'candidate_selection', 'manager_notification', 'feedback_report'],
  },
  {
    category: 'Interview',
    icon: <ScheduleIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
    variables: ['{{ interview_date }}', '{{ interview_url }}'],
    appliesTo: ['interview_invite', 'manager_notification', 'feedback_report'],
  },
  {
    category: 'Score',
    icon: <StarIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
    variables: ['{{ company_admin_name }}', '{{ score_percentage }}'],
    appliesTo: ['manager_notification'],
  },
  {
    category: 'Rejection',
    icon: <InfoIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />,
    variables: ['{{ rejection_reason }}'],
    appliesTo: ['candidate_rejection'],
  },
];

const SafeIframePreview = ({ html, title }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(html || '<p style="font-family: sans-serif; color: #94a3b8; text-align:center; padding: 60px 20px;">No content available</p>');
        doc.close();
      }
    }
  }, [html]);

  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      minHeight: { xs: 400, md: 550 },
      borderRadius: '12px',
      overflow: 'hidden',
      border: '1px solid #e2e8f0',
      display: 'flex',
      justifyContent: 'center',
      background: 'white',
      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
    }}>
      <iframe
        ref={iframeRef}
        title={title}
        style={{
          width: '100%',
          maxWidth: '100%',
          height: '100%',
          minHeight: '100%',
          border: 'none',
          background: 'white'
        }}
      />
    </Box>
  );
};

const getSnippet = (html) => {
  if (!html) return '';
  // Remove style and script tags completely before stripping other tags
  let text = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ');
  text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ');
  // Strip remaining HTML tags and extra spaces
  text = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > 140 ? text.substring(0, 140) + '...' : text;
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EmailTemplates = () => {
  const { t } = useTranslation();
  const { userDetails } = useUser();
  const MAX_TEMPLATES = parseInt(process.env.REACT_APP_EMAIL_TEMPLATE_MAX_COUNT || '5', 10) || 5;
  const [selectedType, setSelectedType] = useState(TEMPLATE_TYPES[0]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'editor'
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Send Test Email state
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testEmailCc, setTestEmailCc] = useState('');
  const [testEmailSubject, setTestEmailSubject] = useState('');
  const [testEmailIncludeChanges, setTestEmailIncludeChanges] = useState(true);
  const [testEmailUseSampleData, setTestEmailUseSampleData] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);

  // Storage for our fetched templates
  const [templates, setTemplates] = useState([]);
  const [defaultTemplate, setDefaultTemplate] = useState({ subject: '', html_content: '' });

  const used = Array.isArray(templates) ? templates.filter(t => t.template_type === selectedType.id).length : 0;
  const isLimitReached = used >= MAX_TEMPLATES;

  // Editor state
  const [editingTemplate, setEditingTemplate] = useState({ name: '', subject: '', html_content: '', is_active: false, visual_style: 'minimal' });
  const [activeTab, setActiveTab] = useState(0); // 0 = Preview, 1 = Edit HTML
  const [previewHtml, setPreviewHtml] = useState('');
  const [displayStyle, setDisplayStyle] = useState('grid'); // 'grid' or 'list' for the main library view
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewTab, setViewTab] = useState(0); // 0: Active, 1: Archived

  // Custom colors and style states for Editor
  const [selectedStyleId, setSelectedStyleId] = useState('minimal');
  const [headerColor, setHeaderColor] = useState('#2563eb');
  const [footerColor, setFooterColor] = useState('#f8fafc');
  const [emailColor, setEmailColor] = useState('#2563eb');

  // Fullscreen Preview states
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [previewStyleId, setPreviewStyleId] = useState('minimal');
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [previewClient, setPreviewClient] = useState('gmail');
  const [previewHeaderColor, setPreviewHeaderColor] = useState('#2563eb');
  const [previewFooterColor, setPreviewFooterColor] = useState('#f8fafc');
  const [previewEmailColor, setPreviewEmailColor] = useState('#2563eb');
  const [previewBrandingMode, setPreviewBrandingMode] = useState('co_branded');
  const [previewHeaderMode, setPreviewHeaderMode] = useState('logo_and_text');
  const [previewLogoUrl, setPreviewLogoUrl] = useState('');

  // Live Variables state
  const [liveVariables, setLiveVariables] = useState({
    candidate_name: 'John Doe',
    company_name: 'AptiView',
    job_title: 'Senior Software Engineer',
    interview_date: '24 October 2026',
    interview_time: '10:00 AM (IST)',
    interview_link: 'https://aptiview.ai/interview/123',
    company_admin_name: 'Aman Dwivedi',
    score_percentage: '85.50%',
    recruiter_name: '',
    support_email: 'support@aptiview.ai',
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: () => { },
    confirmButtonText: ''
  });

  const fetchTemplatesByType = useCallback(async (typeId, includeArchived = false) => {
    try {
      setLoading(true);
      const url = includeArchived
        ? `/email-templates/by_type/?template_type=${typeId}&include_archived=true`
        : `/email-templates/by_type/?template_type=${typeId}`;
      const res = await api.get(url);
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Error fetching templates by type:', e);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDefault = useCallback(async (typeId) => {
    try {
      const response = await api.get(`/email-templates/get_default/?template_type=${typeId}`);
      const data = response.data;
      const tpl = {
        template_type: typeId,
        subject: data.subject || '',
        html_content: data.html_content || '',
        isSystemDefault: true,
      };
      setDefaultTemplate(tpl);
      return tpl;
    } catch (error) {
      console.error('Error fetching default template:', error);
      return null;
    }
  }, []);

  const renderPreviewHtml = async (htmlContent, styleId, colors = null, customVars = null) => {
    try {
      const parts = (styleId || 'minimal').split('|');
      const baseStyle = parts[0];
      const hColor = colors ? colors.header_color : (parts[1] || headerColor);
      const fColor = colors ? colors.footer_color : (parts[2] || footerColor);
      const eColor = colors ? colors.email_color : (parts[3] || emailColor);

      const fullStyleStr = `${baseStyle}|${hColor}|${fColor}|${eColor}`;

      const previewResp = await api.post('/email-templates/preview/', {
        html_content: htmlContent || '',
        visual_style: fullStyleStr,
        dummy_context: customVars || liveVariables
      });
      setPreviewHtml(previewResp.data.rendered_html || '');
    } catch (e) {
      console.warn('Custom preview rendering failed', e);
      setPreviewHtml(htmlContent || '');
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchTemplatesByType(selectedType.id, true);
      await fetchDefault(selectedType.id);
    };
    init();
  }, [fetchTemplatesByType, fetchDefault, selectedType.id, viewTab]);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setViewMode('list');
    setViewTab(0); // Reset to active tab when changing type
    fetchTemplatesByType(type.id, true);
    fetchDefault(type.id);
  };

  const openEditor = async (templateObj, startInEdit = false) => {
    setLoading(true);

    // Parse visual style colors
    const visualStyleStr = templateObj.visual_style || 'minimal';
    const parts = visualStyleStr.split('|');
    const baseStyle = parts[0] || 'minimal';
    const hColor = parts[1] || '#2563eb';
    const fColor = parts[2] || '#f8fafc';
    const eColor = parts[3] || '#2563eb';

    setEditingTemplate({
      ...templateObj,
      template_type: selectedType.id,
      visual_style: visualStyleStr
    });

    setSelectedStyleId(baseStyle);
    setHeaderColor(hColor);
    setFooterColor(fColor);
    setEmailColor(eColor);

    setActiveTab(startInEdit ? 1 : 0);
    setViewMode('editor');

    if (templateObj.html_content) {
      await renderPreviewHtml(
        templateObj.html_content,
        visualStyleStr,
        { header_color: hColor, footer_color: fColor, email_color: eColor },
        liveVariables
      );
    } else {
      setPreviewHtml('');
    }
    setLoading(false);
  };

  const handleSave = async (makeActive = false) => {
    const templateToSave = previewDialogOpen ? previewTemplate : editingTemplate;
    if (!templateToSave || !templateToSave.subject || !templateToSave.subject.trim()) {
      toast.warning(t('emailTemplates.validation.subjectRequired', 'Please enter a subject line'));
      return;
    }

    setSaving(true);
    try {
      let styleId = selectedStyleId;
      let hColor = headerColor;
      let fColor = footerColor;
      let eColor = emailColor;
      let bMode = 'co_branded';
      let hMode = 'logo_and_text';
      let logoUrl = '';

      if (previewDialogOpen) {
        styleId = previewStyleId;
        hColor = previewHeaderColor;
        fColor = previewFooterColor;
        eColor = previewEmailColor;
        bMode = previewBrandingMode;
        hMode = previewHeaderMode;
        logoUrl = previewLogoUrl;
      }

      // Sanitize payload to exclude UI-only helper fields like isSystemDefault
      const { isSystemDefault, ...sanitizedTemplate } = templateToSave;

      const payload = {
        name: sanitizedTemplate.name || `Custom ${selectedType.label}`,
        ...sanitizedTemplate,
        template_type: selectedType.id,
        visual_style: `${styleId}|${hColor}|${fColor}|${eColor}|${bMode}|${hMode}|${logoUrl}`,
        ...(makeActive ? { is_active: true } : {})
      };

      let savedId = templateToSave.id;
      if (templateToSave.id) {
        await api.put(`/email-templates/${templateToSave.id}/`, payload);
        toast.success(t('emailTemplates.toast.updated', 'Template updated successfully'));
      } else {
        const res = await api.post('/email-templates/', payload);
        savedId = res.data?.id;
        toast.success(t('emailTemplates.toast.created', 'New template created successfully'));
      }

      if (makeActive && savedId) {
        await api.post(`/email-templates/${savedId}/set_active/`);
      }

      await fetchTemplatesByType(selectedType.id, true);
      setViewMode('list');
    } catch (error) {
      console.error('Error saving template:', error);
      let errorMsg = t('emailTemplates.toast.saveFailed', 'Failed to save template');
      if (error?.response?.data) {
        if (typeof error.response.data === 'object') {
          errorMsg = Object.entries(error.response.data)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join(' | ');
        } else if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        }
      }
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (id) => {
    try {
      setLoading(true);
      await api.post(`/email-templates/${id}/set_active/`);
      toast.success(t('emailTemplates.toast.setActive', 'Template set as active'));
      await fetchTemplatesByType(selectedType.id, true);
    } catch (e) {
      console.error('Error setting active template:', e);
      toast.error(t('emailTemplates.toast.setActiveFailed', 'Failed to set active template'));
    } finally {
      setLoading(false);
    }
  };

  const setSystemDefaultActive = async () => {
    try {
      setLoading(true);
      await api.post('/email-templates/deactivate_all/', {
        template_type: selectedType.id
      });
      toast.success(t('emailTemplates.toast.systemDefaultActivated', 'System Default set as active'));
      await fetchTemplatesByType(selectedType.id, true);
    } catch (e) {
      console.error('Error setting default active:', e);
      toast.error(t('emailTemplates.toast.systemDefaultActivateFailed', 'Failed to activate default template'));
    } finally {
      setLoading(false);
    }
  };

  const archiveTemplate = async (id) => {
    setConfirmDialog({
      open: true,
      title: t('emailTemplates.confirm.archiveTitle', 'Archive Template'),
      content: t('emailTemplates.confirm.archive', 'This template will still count toward your {{count}}-template limit. Are you sure you want to archive it?', { count: MAX_TEMPLATES }),
      confirmButtonText: 'Archive',
      onConfirm: async () => {
        try {
          setLoading(true);
          await api.patch(`/email-templates/${id}/`, { is_archived: true, is_active: false });
          toast.success(t('emailTemplates.toast.archived', 'Template archived successfully'));
          await fetchTemplatesByType(selectedType.id, true);
        } catch (e) {
          console.error('Error archiving template:', e);
          toast.error(t('emailTemplates.toast.archiveFailed', 'Failed to archive template'));
        } finally {
          setLoading(false);
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      }
    });
  };

  const restoreTemplate = async (id) => {
    try {
      setLoading(true);
      // Use dedicated endpoint so backend can enforce limits and return clear errors
      await api.post(`/email-templates/${id}/unarchive/`);
      toast.success(t('emailTemplates.toast.restored', 'Template restored to active list'));
      await fetchTemplatesByType(selectedType.id, true);
    } catch (e) {
      console.error('Error restoring template:', e);
      const msg = e?.response?.data?.detail || e?.response?.data || 'Failed to restore template';
      toast.error(msg || t('emailTemplates.toast.restoreFailed', 'Failed to restore template'));
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (id) => {
    setConfirmDialog({
      open: true,
      title: t('emailTemplates.confirm.deleteTitle', 'Delete Template'),
      content: t('emailTemplates.confirm.delete', 'Are you sure you want to permanently delete this template? This action cannot be undone.'),
      confirmButtonText: 'Delete',
      onConfirm: async () => {
        try {
          setLoading(true);
          await api.delete(`/email-templates/${id}/`);
          toast.success(t('emailTemplates.toast.deleted', 'Template deleted successfully'));
          await fetchTemplatesByType(selectedType.id, true);
        } catch (e) {
          console.error('Error deleting template:', e);
          toast.error(t('emailTemplates.toast.deleteFailed', 'Failed to delete template'));
        } finally {
          setLoading(false);
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      }
    });
  };

  const handleReset = () => {
    setConfirmDialog({
      open: true,
      title: t('emailTemplates.confirm.resetTitle', 'Reset Template'),
      content: t('emailTemplates.confirm.reset', 'Reset to system default? Your customized template will be reverted.'),
      confirmButtonText: 'Reset',
      onConfirm: () => {
        setEditingTemplate({
          id: editingTemplate.id,
          template_type: selectedType.id,
          subject: defaultTemplate.subject,
          html_content: defaultTemplate.html_content,
          visual_style: 'minimal',
          is_active: true,
        });
        renderPreviewHtml(defaultTemplate.html_content, 'minimal');
        setConfirmDialog(prev => ({ ...prev, open: false }));
      }
    });
  };

  const handlePreviewRefresh = async () => {
    await renderPreviewHtml(editingTemplate.html_content, editingTemplate.visual_style);
    setActiveTab(0);
  };

  const handleStyleChange = async (styleId) => {
    setSelectedStyleId(styleId);
    await renderPreviewHtml(editingTemplate.html_content, `${styleId}|${headerColor}|${footerColor}|${emailColor}`);
  };

  const handleColorChange = async (type, val) => {
    let newH = headerColor;
    let newF = footerColor;
    let newE = emailColor;
    if (type === 'header') { newH = val; setHeaderColor(val); }
    if (type === 'footer') { newF = val; setFooterColor(val); }
    if (type === 'email') { newE = val; setEmailColor(val); }
    await renderPreviewHtml(editingTemplate.html_content, `${selectedStyleId}|${newH}|${newF}|${newE}`);
  };

  const handleOpenPreviewDialog = async (templateObj) => {
    const visualStyleStr = templateObj.visual_style || 'minimal';
    const parts = visualStyleStr.split('|');
    const baseStyle = parts[0] || 'minimal';
    const hColor = parts[1] || '#2563eb';
    const fColor = parts[2] || '#f8fafc';
    const eColor = parts[3] || '#2563eb';
    const bMode = parts[4] || 'co_branded';
    const hMode = parts[5] || 'logo_and_text';
    const logoUrl = parts.slice(6).join('|') || '';

    setPreviewTemplate(templateObj);
    setPreviewStyleId(baseStyle);
    setPreviewHeaderColor(hColor);
    setPreviewFooterColor(fColor);
    setPreviewEmailColor(eColor);
    setPreviewBrandingMode(bMode);
    setPreviewHeaderMode(hMode);
    setPreviewLogoUrl(logoUrl);
    setPreviewDevice('desktop');
    setPreviewDialogOpen(true);

    await renderPreviewHtmlForDialog(templateObj.html_content, baseStyle, hColor, fColor, eColor, bMode, hMode, logoUrl, liveVariables);
  };

  const renderPreviewHtmlForDialog = async (htmlContent, styleId, hColor, fColor, eColor, bMode, hMode, logoUrl, vars) => {
    try {
      const fullStyleStr = `${styleId}|${hColor}|${fColor}|${eColor}|${bMode}|${hMode}|${logoUrl}`;
      const previewResp = await api.post('/email-templates/preview/', {
        html_content: htmlContent || '',
        visual_style: fullStyleStr,
        dummy_context: vars
      });
      setPreviewHtml(previewResp.data.rendered_html || '');
    } catch (e) {
      console.warn('Dialog preview rendering failed', e);
      setPreviewHtml(htmlContent || '');
    }
  };

  const handleDialogStyleChange = async (styleId) => {
    setPreviewStyleId(styleId);
    await renderPreviewHtmlForDialog(previewTemplate.html_content, styleId, previewHeaderColor, previewFooterColor, previewEmailColor, previewBrandingMode, previewHeaderMode, previewLogoUrl, liveVariables);
  };

  const handleDialogColorChange = async (type, val) => {
    let newH = previewHeaderColor;
    let newF = previewFooterColor;
    let newE = previewEmailColor;
    if (type === 'header') { newH = val; setPreviewHeaderColor(val); }
    if (type === 'footer') { newF = val; setPreviewFooterColor(val); }
    if (type === 'email') { newE = val; setPreviewEmailColor(val); }
    await renderPreviewHtmlForDialog(previewTemplate.html_content, previewStyleId, newH, newF, newE, previewBrandingMode, previewHeaderMode, previewLogoUrl, liveVariables);
  };

  const handleDialogVariableChange = async (name, val) => {
    const updatedVars = { ...liveVariables, [name]: val };
    setLiveVariables(updatedVars);
    await renderPreviewHtmlForDialog(previewTemplate.html_content, previewStyleId, previewHeaderColor, previewFooterColor, previewEmailColor, previewBrandingMode, previewHeaderMode, previewLogoUrl, updatedVars);
  };

  const handleDialogBrandingModeChange = async (val) => {
    setPreviewBrandingMode(val);
    await renderPreviewHtmlForDialog(previewTemplate.html_content, previewStyleId, previewHeaderColor, previewFooterColor, previewEmailColor, val, previewHeaderMode, previewLogoUrl, liveVariables);
  };

  const handleDialogHeaderModeChange = async (val) => {
    setPreviewHeaderMode(val);
    await renderPreviewHtmlForDialog(previewTemplate.html_content, previewStyleId, previewHeaderColor, previewFooterColor, previewEmailColor, previewBrandingMode, val, previewLogoUrl, liveVariables);
  };

  const handleDialogLogoChange = async (val) => {
    setPreviewLogoUrl(val);
    await renderPreviewHtmlForDialog(previewTemplate.html_content, previewStyleId, previewHeaderColor, previewFooterColor, previewEmailColor, previewBrandingMode, previewHeaderMode, val, liveVariables);
  };

  const handleSendTestEmail = () => {
    setTestEmailSubject(`${previewTemplate?.subject || editingTemplate?.subject || 'Interview Invitation'} (Preview)`);
    setTestEmailTo(userDetails?.email || '');
    setTestEmailCc('');
    setTestEmailIncludeChanges(true);
    setTestEmailUseSampleData(true);
    setTestEmailOpen(true);
  };

  const executeSendTestEmail = async () => {
    if (!testEmailTo.trim()) {
      toast.warning('Please enter at least one recipient email address.');
      return;
    }

    setSendingTest(true);
    try {
      let htmlToSend = previewTemplate?.html_content || '';
      let styleToSend = `${previewStyleId}|${previewHeaderColor}|${previewFooterColor}|${previewEmailColor}|${previewBrandingMode}|${previewHeaderMode}|${previewLogoUrl}`;

      if (!testEmailIncludeChanges && previewTemplate?.id) {
        const savedTemplate = templates.find(t => t.id === previewTemplate.id);
        if (savedTemplate) {
          htmlToSend = savedTemplate.html_content;
          styleToSend = savedTemplate.visual_style;
        }
      }

      await api.post('/email-templates/send_test/', {
        html_content: htmlToSend,
        visual_style: styleToSend,
        subject: testEmailSubject,
        to_emails: testEmailTo,
        cc_emails: testEmailCc,
        use_sample_data: testEmailUseSampleData,
        dummy_context: testEmailUseSampleData ? liveVariables : {}
      });

      toast.success('Test email sent successfully!');
      setTestEmailOpen(false);
    } catch (e) {
      console.error('Failed to send test email:', e);
      const msg = e?.response?.data?.error || e?.response?.data?.detail || 'Failed to send test email';
      toast.error(msg);
    } finally {
      setSendingTest(false);
    }
  };

  const handleCopyPreviewHtml = () => {
    navigator.clipboard.writeText(previewHtml);
    toast.success('Rendered HTML template copied to clipboard!');
  };

  const handleDownloadPreviewHtml = () => {
    const blob = new Blob([previewHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${previewTemplate?.name || 'template'}_preview.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Email template file downloaded successfully!');
  };

  const handleCopyVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    toast.info(`Copied: ${variable}`, { autoClose: 1500 });
  };

  const handleDragStart = (e, variable) => {
    e.dataTransfer.setData('text/plain', variable);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const filteredTemplates = Array.isArray(templates) ? templates.filter(t => t.template_type === selectedType.id) : [];

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', p: { xs: 1, md: 2 }, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Updated Page Header - Aligned with the content below */}
      <Box
        sx={{
          px: { xs: 4, md: 4 }, // match px-4
          py: { xs: '12px', md: '12px' }, // match py-3 (~12px)
          mb: { xs: 2, md: 3 },
          color: 'white',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, rgb(14,165,233) 0%, rgb(217,70,239) 100%)',
          width: '100%'
        }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              fontWeight: 600,
              mb: 0.5,
              color: 'white',
              fontFamily: "'Poppins', sans-serif"
            }}>
              {t('settings.emailTemplates', 'Email Templates')}
            </Typography>
            <Typography sx={{
              fontSize: '0.75rem',
              color: 'rgba(219,234,254,0.95)',
              fontFamily: "'Poppins', sans-serif",
              display: { xs: 'none', sm: 'block' }
            }}>
              {t('settings.emailTemplatesSub', 'Customize and manage email communications for recruitment phases')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Layout Container - Using display flex for Sidebar + Content */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 3 },
        flexGrow: 1,
        width: '100%',
        minHeight: 0 // Prevents flex children from stretching parent
      }}>
        {/* LEFT SIDEBAR - Template Library - Fixed Width on Desktop */}
        <Box sx={{
          width: {
            xs: '100%',
            md: sidebarCollapsed ? '70px' : '260px',
            lg: sidebarCollapsed ? '80px' : '280px'
          },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          position: { md: 'sticky' },
          top: { md: '84px' }, // Offset for navbar
          height: { md: 'fit-content' },
          zIndex: 10,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          background: 'white',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.06)',
          p: sidebarCollapsed ? 1 : 0
        }}>
          <Box sx={{
            px: sidebarCollapsed ? 0 : 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarCollapsed ? 'center' : 'space-between',
            borderBottom: '1px solid #f1f5f9',
            mb: 1
          }}>
            {!sidebarCollapsed && (
              <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', letterSpacing: '0.8px' }}>
                TEMPLATE LIBRARY
              </Typography>
            )}
            <Tooltip title={sidebarCollapsed ? t('emailTemplates.expandSidebar', 'Expand Sidebar') : t('emailTemplates.collapseSidebar', 'Collapse Sidebar')}>
              <IconButton
                size="small"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                sx={{
                  color: '#64748b',
                  display: { xs: 'none', md: 'flex' }, // Hide on mobile
                  backgroundColor: sidebarCollapsed ? '#f8fafc' : 'transparent',
                  '&:hover': { backgroundColor: '#f1f5f9', color: '#7c3aed' }
                }}
              >
                {sidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
          <List sx={{
            p: sidebarCollapsed ? 0 : 1,
            display: { xs: 'flex', md: 'block' },
            flexDirection: { xs: 'row', md: 'column' },
            overflowX: { xs: 'auto', md: 'visible' },
            gap: 0.5,
            '&::-webkit-scrollbar': { height: '4px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '10px' }
          }}>
            {TEMPLATE_TYPES.map((type) => (
              <ListItem key={type.id} disablePadding sx={{ mb: 0.5 }}>
                <Tooltip
                  title={t(`emailTemplates.types.${type.id}.label`, { defaultValue: type.label })}
                  placement="right"
                  arrow
                  FadeProps={{ timeout: 600 }}
                >
                  <ListItemButton
                    onClick={() => handleTypeSelect(type)}
                    sx={{
                      px: sidebarCollapsed ? 1 : 2,
                      py: 1.2,
                      borderRadius: '12px',
                      position: 'relative',
                      backgroundColor: selectedType.id === type.id ? 'rgba(124, 58, 237, 0.08)' : 'transparent',
                      border: '1px solid',
                      borderColor: selectedType.id === type.id ? 'rgba(124, 58, 237, 0.2)' : 'transparent',
                      '&:hover': {
                        backgroundColor: selectedType.id === type.id ? 'rgba(124, 58, 237, 0.12)' : '#f8fafc',
                        transform: 'translateX(4px)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Active Indicator Bar */}
                    {selectedType.id === type.id && (
                      <Box sx={{
                        position: 'absolute',
                        left: 0,
                        top: '20%',
                        bottom: '20%',
                        width: '4px',
                        background: 'linear-gradient(to bottom, #8b5cf6, #d946ef)',
                        borderRadius: '0 4px 4px 0'
                      }} />
                    )}

                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      width: sidebarCollapsed ? 'auto' : '100%',
                      zIndex: 1
                    }}>
                      <Box sx={{
                        color: selectedType.id === type.id ? '#7c3aed' : '#94a3b8',
                        display: 'flex',
                        minWidth: 24,
                        justifyContent: 'center',
                        transition: 'color 0.3s',
                        '& svg': { fontSize: 22 }
                      }}>
                        {type.icon}
                      </Box>
                      {!sidebarCollapsed && (
                        <Typography sx={{
                          fontWeight: selectedType.id === type.id ? 700 : 600,
                          fontSize: '0.875rem',
                          color: selectedType.id === type.id ? '#1e293b' : '#64748b',
                          transition: 'color 0.3s'
                        }}>
                          {t(`emailTemplates.types.${type.id}.label`, { defaultValue: type.label })}
                        </Typography>
                      )}
                    </Box>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* MAIN CONTENT AREA - Fluid Width */}
        <Box sx={{
          flexGrow: 1,
          minWidth: 0, // Critical for permitting sub-components to handle their own overflow
          pb: 4
        }}>
          {viewMode === 'list' ? (
            <Fade in>
              <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', background: 'white', p: { xs: 2.5, md: 4 }, minHeight: 850 }}>
                {/* List View Header */}
                <Box sx={{ mb: 4 }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', letterSpacing: '0.8px' }}>
                    {t('emailTemplates.breadcrumb.main', 'TEMPLATES')} &nbsp;&gt;&nbsp; <span style={{ color: '#9333ea', fontWeight: 700 }}>{t('emailTemplates.breadcrumb.highlight', 'RECRUITMENT')}</span>
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mt: 1, gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                        {selectedType.label} Templates
                      </Typography>
                      <Tabs
                        value={viewTab}
                        onChange={(e, v) => setViewTab(v)}
                        sx={{
                          minHeight: '40px',
                          background: '#f8fafc',
                          p: 0.5,
                          borderRadius: '12px',
                          '& .MuiTab-root': {
                            minHeight: '32px',
                            py: 1,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            textTransform: 'none',
                            borderRadius: '10px',
                            minWidth: 'auto',
                            px: 3,
                            color: '#64748b',
                            '&.Mui-selected': {
                              color: '#7c3aed',
                              background: 'white',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            },
                            transition: 'all 0.2s'
                          },
                          '& .MuiTabs-indicator': {
                            display: 'none'
                          }
                        }}
                      >
                        <Tab label={`All (${templates.filter(t => !t.is_archived).length + (viewTab === 0 ? 1 : 0)})`} />
                        <Tab label="Archived" />
                      </Tabs>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' }, justifyContent: 'space-between' }}>
                      {/* Grid/List Toggle */}
                      <Box sx={{ display: 'flex', gap: 0.5, background: '#f1f5f9', p: 0.5, borderRadius: '10px' }}>
                        <IconButton
                          size="small"
                          onClick={() => setDisplayStyle('grid')}
                          sx={{
                            borderRadius: '8px',
                            background: displayStyle === 'grid' ? 'white' : 'transparent',
                            boxShadow: displayStyle === 'grid' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                            transition: 'all 0.2s',
                            '&:hover': { background: displayStyle === 'grid' ? 'white' : 'rgba(0,0,0,0.05)' }
                          }}
                        >
                          <GridViewIcon sx={{ fontSize: 18, color: displayStyle === 'grid' ? '#6366f1' : '#64748b' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDisplayStyle('list')}
                          sx={{
                            borderRadius: '8px',
                            background: displayStyle === 'list' ? 'white' : 'transparent',
                            boxShadow: displayStyle === 'list' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                            transition: 'all 0.2s',
                            '&:hover': { background: displayStyle === 'list' ? 'white' : 'rgba(0,0,0,0.05)' }
                          }}
                        >
                          <ViewListIcon sx={{ fontSize: 18, color: displayStyle === 'list' ? '#6366f1' : '#64748b' }} />
                        </IconButton>
                      </Box>

                      <Tooltip title={isLimitReached ? t('emailTemplates.limitReached', { count: MAX_TEMPLATES, defaultValue: `You’ve reached your limit of ${MAX_TEMPLATES} templates (including archived). Please permanently delete one to create a new template.` }) : ""}>
                        <span>
                          <Button
                            variant="contained"
                            disabled={isLimitReached}
                            onClick={() => {
                              const localizedTypeLabel = t(`emailTemplates.types.${selectedType.id}.label`, { defaultValue: selectedType.label });
                              openEditor({
                                name: t('emailTemplates.newName', { typeLabel: localizedTypeLabel, defaultValue: `New ${localizedTypeLabel}` }),
                                subject: t('emailTemplates.newSubject', { typeLabel: localizedTypeLabel, defaultValue: `${localizedTypeLabel}` }),
                                html_content: `<p>Hello {{ candidate_name }},</p><p>Enter your email content here...</p>`,
                                visual_style: 'minimal'
                              }, true);
                            }}
                            sx={{
                              borderRadius: '10px',
                              textTransform: 'none',
                              fontWeight: 700,
                              background: isLimitReached ? '#e2e8f0' : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                              px: { xs: 1.5, sm: 3 },
                              py: 1,
                              fontSize: { xs: '0.8rem', sm: '0.875rem' },
                              boxShadow: isLimitReached ? 'none' : '0 4px 14px 0 rgba(139, 92, 246, 0.39)',
                              '&:hover': {
                                background: isLimitReached ? '#e2e8f0' : 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                                boxShadow: isLimitReached ? 'none' : '0 6px 20px rgba(139, 92, 246, 0.23)',
                                transform: isLimitReached ? 'none' : 'translateY(-1px)'
                              },
                              transition: 'all 0.2s'
                            }}
                          >
                            {t('emailTemplates.createTemplate', '+ Create Template')}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}><CircularProgress /></Box>
                ) : displayStyle === 'grid' ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                    {/* Always show the System Default Template */}
                    <Box sx={{ width: '100%' }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: '20px',
                          border: !templates.some(t => t.is_active) ? '2px solid #22c55e' : '1px solid #e2e8f0',
                          borderLeft: '6px solid #f59e0b',
                          backgroundColor: !templates.some(t => t.is_active) ? '#f0fdf4' : '#fffdfa',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          '&:hover': {
                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                            transform: 'translateY(-4px)',
                            borderColor: !templates.some(t => t.is_active) ? '#22c55e' : '#cbd5e1'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' } }}>
                          <Box sx={{ flex: 1, width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1.1rem' }}>{t('emailTemplates.systemDefault', 'System Default')}</Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {!templates.some(t => t.is_active) && (
                                  <Chip
                                    label={t('emailTemplates.chips.active', 'ACTIVE')}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '0.65rem',
                                      fontWeight: 900,
                                      backgroundColor: '#dcfce7',
                                      color: '#16a34a',
                                      borderRadius: '6px'
                                    }}
                                  />
                                )}
                                <Chip
                                  label={t('emailTemplates.chips.default', 'DEFAULT')}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.65rem',
                                    fontWeight: 900,
                                    backgroundColor: '#fef3c7',
                                    color: '#d97706',
                                    borderRadius: '6px'
                                  }}
                                  icon={<StarIcon sx={{ fontSize: '12px !important', color: '#d97706' }} />}
                                />
                              </Box>
                              <Chip
                                label={t('emailTemplates.chips.minimal', 'MINIMAL')}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  backgroundColor: '#f1f5f9',
                                  color: '#475569',
                                  borderRadius: '6px'
                                }}
                              />
                            </Box>
                            <Typography sx={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: 600, mb: 1 }}>
                              {t('emailTemplates.subjectLabel', 'Subject:')} {defaultTemplate.subject || t('emailTemplates.defaultSubjectFallback', 'Standard Invitation')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mb: 2, fontStyle: 'italic', lineHeight: 1.5 }}>
                              "{getSnippet(defaultTemplate.html_content)}"
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 1, ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                            <Tooltip title={t('emailTemplates.preview', 'Preview')}>
                              <IconButton onClick={() => handleOpenPreviewDialog(defaultTemplate)} sx={{ color: '#64748b', border: '1.5px solid #f1f5f9', '&:hover': { backgroundColor: '#f8fafc' } }}>
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {templates.some(t => t.is_active) && (
                              <Tooltip title={t('emailTemplates.setAsActive', 'Set as Active')}>
                                <IconButton onClick={setSystemDefaultActive} sx={{ color: '#10b981', border: '1.5px solid rgba(16,185,129,0.1)', '&:hover': { backgroundColor: 'rgba(16,185,129,0.05)' } }}>
                                  <StarIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    </Box>

                    {/* Custom Templates */}
                    {templates.filter(t => !t.is_default && (viewTab === 1 ? t.is_archived : !t.is_archived)).map((tpl) => (
                      <Box sx={{ width: '100%' }} key={tpl.id}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: '20px',
                            border: tpl.is_active ? '2px solid #22c55e' : '1px solid #e2e8f0',
                            borderLeft: tpl.is_active ? '6px solid #22c55e' : (tpl.is_default ? '6px solid #f59e0b' : '6px solid #8b5cf6'),
                            backgroundColor: tpl.is_active ? '#f0fdf4' : (tpl.is_default ? '#fffdfa' : '#fff'),
                            opacity: tpl.is_archived ? 0.7 : 1,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                              transform: 'translateY(-4px)',
                              borderColor: tpl.is_active ? '#22c55e' : '#cbd5e1'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' } }}>
                            <Box sx={{ flex: 1, width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontWeight: 800, color: tpl.is_archived ? '#94a3b8' : '#1e293b', fontSize: '1.1rem' }}>
                                  {tpl.is_default ? t('emailTemplates.systemDefault', 'System Default') : (tpl.name || t('emailTemplates.customTemplate', 'Custom Template'))}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {tpl.is_active && (
                                    <Chip
                                      label={t('emailTemplates.chips.active', 'ACTIVE')}
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontSize: '0.65rem',
                                        fontWeight: 900,
                                        backgroundColor: '#dcfce7',
                                        color: '#16a34a',
                                        borderRadius: '6px'
                                      }}
                                    />
                                  )}
                                  {tpl.is_archived && (
                                    <Chip
                                      label={t('emailTemplates.chips.archived', 'ARCHIVED')}
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontSize: '0.65rem',
                                        fontWeight: 900,
                                        backgroundColor: '#f1f5f9',
                                        color: '#64748b',
                                        borderRadius: '6px'
                                      }}
                                    />
                                  )}
                                </Box>
                                <Chip
                                  label={(tpl.visual_style || 'minimal').split('|')[0].toUpperCase()}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    backgroundColor: '#f1f5f9',
                                    color: '#475569',
                                    borderRadius: '6px'
                                  }}
                                />
                              </Box>
                              <Typography sx={{ fontSize: '0.9rem', color: tpl.is_archived ? '#94a3b8' : '#1e293b', fontWeight: 600, mb: 1 }}>
                                Subject: {tpl.subject}
                              </Typography>
                              <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mb: 1.5, fontStyle: 'italic', lineHeight: 1.5 }}>
                                "{getSnippet(tpl.html_content)}"
                              </Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ScheduleIcon sx={{ fontSize: 14 }} /> Updated {tpl.updated_at ? new Date(tpl.updated_at).toLocaleDateString() : 'recently'}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1, ml: { xs: 0, sm: 2 }, mt: { xs: 1.5, sm: 0 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                              <Tooltip title="Preview">
                                <IconButton onClick={() => handleOpenPreviewDialog(tpl)} size="small" sx={{ color: '#64748b', border: '1.5px solid #f1f5f9', '&:hover': { backgroundColor: '#f8fafc' } }}>
                                  <PreviewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {!tpl.is_archived && (
                                <Tooltip title="Edit HTML">
                                  <IconButton onClick={() => openEditor(tpl, true)} size="small" sx={{ color: '#8b5cf6', border: '1.5px solid rgba(139,92,246,0.1)', '&:hover': { backgroundColor: 'rgba(139,92,246,0.05)' } }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {!tpl.is_active && !tpl.is_archived && (
                                <Tooltip title="Set as Active">
                                  <IconButton onClick={() => setActive(tpl.id)} size="small" sx={{ color: '#10b981', border: '1.5px solid rgba(16,185,129,0.1)', '&:hover': { backgroundColor: 'rgba(16,185,129,0.05)' } }}>
                                    <StarIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {tpl.is_archived ? (
                                <Tooltip title="Restore Template">
                                  <IconButton onClick={() => restoreTemplate(tpl.id)} size="small" sx={{ color: '#10b981', border: '1.5px solid #f1f5f9', '&:hover': { backgroundColor: '#f0fdf4' } }}>
                                    <RestoreIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                !tpl.is_default && (
                                  <Tooltip title="Archive Template">
                                    <IconButton onClick={() => archiveTemplate(tpl.id)} size="small" sx={{ color: '#94a3b8', border: '1.5px solid #f1f5f9', '&:hover': { backgroundColor: '#fef3c7', color: '#d97706' } }}>
                                      <ArchiveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )
                              )}
                              {!tpl.is_default && (
                                tpl.is_archived ? (
                                  <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={() => deleteTemplate(tpl.id)}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Delete Permanently
                                  </Button>
                                ) : (
                                  <Tooltip title="Delete Template">
                                    <IconButton onClick={() => deleteTemplate(tpl.id)} size="small" sx={{ color: '#94a3b8', border: '1.5px solid #f1f5f9', '&:hover': { backgroundColor: '#fff1f2', color: '#e11d48' } }}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      </Box>
                    ))}

                    {viewTab === 1 && templates.filter(t => t.is_archived).length === 0 && (
                      <Box sx={{ py: 8, textAlign: 'center', background: '#f8fafc', borderRadius: '16px', border: '1px dashed #e2e8f0' }}>
                        <ArchiveIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                        <Typography sx={{ color: '#64748b', fontWeight: 600 }}>No archived templates</Typography>
                        <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>Templates you archive will appear here.</Typography>
                      </Box>
                    )}

                    {/* Create New Placeholder Card */}
                    {viewTab === 0 && (
                      <Box sx={{ width: '100%' }}>
                        <Paper
                          elevation={0}
                          onClick={isLimitReached ? null : () => openEditor({
                            name: `Custom ${selectedType.label}`,
                            subject: `${selectedType.label}`,
                            html_content: `<p>Hello {{ candidate_name }},</p><p>Start writing your custom template...</p>`,
                            visual_style: 'minimal'
                          }, true)}
                          sx={{
                            p: 3,
                            height: '100%',
                            minHeight: 120,
                            borderRadius: '16px',
                            border: isLimitReached ? '2px solid #f1f5f9' : '2px dashed #e2e8f0',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 2,
                            cursor: isLimitReached ? 'default' : 'pointer',
                            backgroundColor: isLimitReached ? '#f8fafc' : 'transparent',
                            transition: 'all 0.2s',
                            '&:hover': isLimitReached ? {} : {
                              borderColor: '#8b5cf6',
                              backgroundColor: 'rgba(139,92,246,0.02)',
                              '& .add-icon': { color: '#8b5cf6', transform: 'scale(1.1)' }
                            }
                          }}
                        >
                          {isLimitReached ? (
                            <>
                              <BlockIcon sx={{ fontSize: 24, color: '#94a3b8' }} />
                              <Box>
                                <Typography sx={{ fontWeight: 700, color: '#94a3b8', fontSize: '0.9rem' }}>Limit Reached</Typography>
                                <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>You’ve reached your limit of {MAX_TEMPLATES} templates (including archived). Please permanently delete one to create a new template.</Typography>
                              </Box>
                            </>
                          ) : (
                            <>
                              <AddIcon className="add-icon" sx={{ fontSize: 32, color: '#94a3b8', transition: 'all 0.2s' }} />
                              <Typography sx={{ fontWeight: 700, color: '#94a3b8', fontSize: '0.9rem' }}>Create New Template</Typography>
                            </>
                          )}
                        </Paper>
                      </Box>
                    )}
                  </Box>
                ) : (
                  /* THE LIST VIEW - "That List" */
                  <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '2fr 3fr 1fr 1fr' }, p: 2, background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b' }}>TEMPLATE NAME</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', display: { xs: 'none', sm: 'block' } }}>SUBJECT</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', display: { xs: 'none', sm: 'block' } }}>STYLE</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>ACTIONS</Typography>
                    </Box>
                    {templates.filter(t => (viewTab === 1 ? t.is_archived : !t.is_archived)).map((tpl) => (
                      <Box
                        key={tpl.id}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr 1fr', sm: '2fr 3fr 1fr 1fr' },
                          p: 2,
                          borderBottom: '1px solid #f1f5f9',
                          alignItems: 'center',
                          opacity: tpl.is_archived ? 0.7 : 1,
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            background: 'rgba(124, 58, 237, 0.04)',
                            transform: 'scale(1.002)',
                            boxShadow: 'inset 4px 0 0 #7c3aed'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 700, color: tpl.is_archived ? '#94a3b8' : '#1e293b', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {tpl.name || 'Untitled'}
                          </Typography>
                          {tpl.is_active && <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22c55e', flexShrink: 0 }} title="Active" />}
                          {tpl.is_archived && <Chip label="Archived" size="small" sx={{ height: 16, fontSize: '0.6rem' }} />}
                        </Box>
                        <Typography sx={{ color: '#64748b', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: { xs: 'none', sm: 'block' } }}>
                          {tpl.subject}
                        </Typography>
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                          <Chip label={(tpl.visual_style || 'minimal').split('|')[0].toUpperCase()} size="small" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700 }} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title="Preview"><IconButton size="small" onClick={() => handleOpenPreviewDialog(tpl)}><PreviewIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                          {!tpl.is_archived && (
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditor(tpl, true)}><EditIcon size="small" sx={{ color: '#8b5cf6' }} /></IconButton></Tooltip>
                          )}
                          {tpl.is_archived ? (
                            <Tooltip title="Restore"><IconButton size="small" onClick={() => restoreTemplate(tpl.id)}><RestoreIcon sx={{ fontSize: 18, color: '#10b981' }} /></IconButton></Tooltip>
                          ) : (
                            !tpl.is_default && (
                              <Tooltip title="Archive"><IconButton size="small" onClick={() => archiveTemplate(tpl.id)}><ArchiveIcon sx={{ fontSize: 18, color: '#f59e0b' }} /></IconButton></Tooltip>
                            )
                          )}
                          {!tpl.is_default && (
                            <Tooltip title={tpl.is_archived ? "Delete Permanently" : "Delete"}><IconButton size="small" onClick={() => deleteTemplate(tpl.id)}><DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} /></IconButton></Tooltip>
                          )}
                        </Box>
                      </Box>
                    ))}
                    {templates.filter(t => (viewTab === 1 ? t.is_archived : !t.is_archived)).length === 0 && (
                      <Box sx={{ p: 6, textAlign: 'center', color: '#94a3b8' }}>
                        <ArchiveIcon sx={{ fontSize: 32, color: '#e2e8f0', mb: 1, display: 'block', mx: 'auto' }} />
                        {viewTab === 1 ? "No archived templates found." : "No templates found."}
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            </Fade>
          ) : (
            <Fade in>
              <Paper elevation={0} sx={{
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                background: 'white',
                overflow: 'hidden',
                height: { md: 'calc(100vh - 120px)', xs: 'auto' },
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
                  {/* Editor Header */}
                  <Box sx={{ px: { xs: 2, md: 3 }, pt: 2, pb: 1.5, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, borderBottom: '1px solid #f1f5f9', background: '#f8fafc', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        startIcon={<KeyboardBackspaceIcon />}
                        onClick={() => setViewMode('list')}
                        sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600, mr: 1, minWidth: 'auto', px: 1 }}
                      />
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          size="small"
                          label={t('emailTemplates.templateNameLabel', 'Template Name')}
                          value={editingTemplate.isSystemDefault ? t('emailTemplates.systemDefault', 'System Default') : editingTemplate.name}
                          onChange={(e) => !editingTemplate.isSystemDefault && setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                          disabled={editingTemplate.isSystemDefault}
                          sx={{
                            width: { xs: '100%', sm: 200, md: 250 },
                            '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: 'white' }
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                      <Chip label={t(`emailTemplates.types.${selectedType.id}.label`, { defaultValue: selectedType.label })} size={"small"} icon={selectedType.icon} variant="outlined" sx={{ fontWeight: 600, display: { xs: 'none', md: 'flex' } }} />
                    </Box>
                  </Box>

                  {/* Visual Style Selector - GAME CHANGER */}
                  <Box sx={{ px: { xs: 2, md: 3 }, py: 2, background: 'linear-gradient(to right, #f8fafc, white)', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', mb: 1.5, letterSpacing: '0.5px' }}>
                      VISUAL STYLE
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      gap: 1.5,
                      overflowX: 'auto',
                      pb: 1,
                      '&::-webkit-scrollbar': { height: '4px' },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '10px' }
                    }}>
                      {EMAIL_STYLES.map((style) => (
                        <Box
                          key={style.id}
                          onClick={() => handleStyleChange(style.id)}
                          sx={{
                            px: 2, py: 1,
                            borderRadius: '12px',
                            cursor: 'pointer',
                            border: '2px solid',
                            whiteSpace: 'nowrap',
                            borderColor: editingTemplate.visual_style === style.id ? style.color : '#e2e8f0',
                            backgroundColor: editingTemplate.visual_style === style.id ? `${style.color}08` : 'white',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            display: 'flex', alignItems: 'center', gap: 1,
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                              borderColor: editingTemplate.visual_style === style.id ? style.color : '#cbd5e1'
                            }
                          }}
                        >
                          <Typography sx={{ fontSize: '1.1rem' }}>{style.icon}</Typography>
                          <Typography sx={{
                            fontWeight: editingTemplate.visual_style === style.id ? 700 : 600,
                            fontSize: '0.85rem',
                            color: editingTemplate.visual_style === style.id ? style.color : '#475569'
                          }}>
                            {style.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* Brand Colors Selector */}
                  <Box sx={{ px: { xs: 2, md: 3 }, py: 2, background: 'linear-gradient(to right, #f8fafc, white)', borderBottom: '1px solid #e2e8f0' }}>
                    <Box
                      onClick={() => {
                        const currentTemp = {
                          ...editingTemplate,
                          visual_style: `${selectedStyleId}|${headerColor}|${footerColor}|${emailColor}`
                        };
                        handleOpenPreviewDialog(currentTemp);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                        p: 2,
                        borderRadius: '12px',
                        backgroundColor: '#f5f3ff',
                        border: '1px solid #ddd6fe',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: '#ede9fe',
                          borderColor: '#c084fc',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.08)'
                        }
                      }}
                    >
                      <LightbulbIcon sx={{ color: '#7c3aed', fontSize: 20, mt: 0.25 }} />
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#5b21b6', mb: 0.5 }}>
                          Real-Time Preview
                        </Typography>
                        <Typography sx={{ fontSize: '0.825rem', color: '#6d28d9', lineHeight: 1.4 }}>
                          Changes appear instantly in the email template preview.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Subject and Editor Layout */}
                  <Box sx={{ p: { xs: 2, md: 3 }, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'auto' }}>

                    {/* Subject */}
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <DescriptionOutlinedIcon sx={{ color: '#64748b', fontSize: 20 }} />
                        <Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '1.05rem' }}>Subject</Typography>
                      </Box>
                      <TextField
                        fullWidth
                        size="medium"
                        value={editingTemplate.subject}
                        disabled={editingTemplate.isSystemDefault}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                        placeholder="Enter email subject line..."
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            '& fieldset': { borderColor: '#e2e8f0', borderWidth: '1px' },
                            '&:hover fieldset': { borderColor: '#cbd5e1' },
                            '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: '2px' }
                          },
                        }}
                      />
                    </Box>

                    {/* Message Body & Toggle */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DescriptionOutlinedIcon sx={{ color: '#64748b', fontSize: 20 }} />
                          <Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '1.05rem' }}>Message Body</Typography>
                        </Box>

                        <Box sx={{
                          display: 'flex',
                          background: '#f1f5f9',
                          borderRadius: '8px',
                          p: 0.5,
                          gap: 0.5
                        }}>
                          <Button
                            onClick={() => {
                              setActiveTab(0);
                              renderPreviewHtml(editingTemplate.html_content, editingTemplate.visual_style);
                            }}
                            startIcon={<PreviewIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              minWidth: 'auto',
                              px: 2,
                              py: 0.5,
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              color: activeTab === 0 ? '#1e293b' : '#64748b',
                              background: activeTab === 0 ? 'white' : 'transparent',
                              boxShadow: activeTab === 0 ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                              borderRadius: '6px',
                              '&:hover': { background: activeTab === 0 ? 'white' : 'rgba(0,0,0,0.05)' }
                            }}
                          >
                            Preview
                          </Button>
                          {!editingTemplate.isSystemDefault && (
                            <Button
                              onClick={() => setActiveTab(1)}
                              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                minWidth: 'auto',
                                px: 2,
                                py: 0.5,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                color: activeTab === 1 ? '#2563eb' : '#64748b',
                                background: activeTab === 1 ? '#e0e7ff' : 'transparent',
                                boxShadow: activeTab === 1 ? '0 1px 3px rgba(37, 99, 235, 0.1)' : 'none',
                                borderRadius: '6px',
                                '&:hover': { background: activeTab === 1 ? '#e0e7ff' : 'rgba(0,0,0,0.05)' }
                              }}
                            >
                              Edit
                            </Button>
                          )}
                          <Button
                            onClick={() => {
                              const currentTemp = {
                                ...editingTemplate,
                                visual_style: `${selectedStyleId}|${headerColor}|${footerColor}|${emailColor}`
                              };
                              handleOpenPreviewDialog(currentTemp);
                            }}
                            startIcon={<SettingsOverscanIcon sx={{ fontSize: 16 }} />}
                            sx={{
                              minWidth: 'auto',
                              px: 2,
                              py: 0.5,
                              textTransform: 'none',
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              color: '#7c3aed',
                              background: 'transparent',
                              borderRadius: '6px',
                              '&:hover': { background: 'rgba(124, 58, 237, 0.05)' }
                            }}
                          >
                            Full Preview
                          </Button>
                        </Box>
                      </Box>

                      {/* Content Area */}
                      <Box sx={{
                        position: 'relative',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 400
                      }}>
                        {loading ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flexGrow: 1, gap: 2 }}>
                            <CircularProgress size={36} />
                            <Typography color="text.secondary" fontSize="0.9rem">Loading Content...</Typography>
                          </Box>
                        ) : activeTab === 0 ? (
                          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <SafeIframePreview html={previewHtml} title="Email Preview" />
                          </Box>
                        ) : (
                          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ minWidth: '100%', width: '100%' }}>
                              <RichTextEditor
                                value={editingTemplate.html_content}
                                onChange={(data) => setEditingTemplate({ ...editingTemplate, html_content: data })}
                              />
                            </Box>
                            <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', mt: 1 }}>
                              Edit the message content above. HTML formatting is handled automatically.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{
                    p: 2.5,
                    borderTop: '1px solid #f1f5f9',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: '#f8fafc',
                    mt: 'auto' // Pull to bottom
                  }}>
                    <Button
                      variant="outlined"
                      onClick={() => setViewMode('list')}
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.2,
                        backgroundColor: 'white',
                        borderColor: '#e2e8f0',
                        color: '#64748b',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        '&:hover': {
                          background: '#f8fafc',
                          borderColor: '#cbd5e1'
                        }
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon sx={{ transform: 'rotate(-45deg)', fontSize: 18 }} />}
                      onClick={() => handleSave()}
                      disabled={saving || editingTemplate.isSystemDefault}
                      sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 4,
                        py: 1.2,
                        background: '#2563eb',
                        boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
                        '&:hover': {
                          background: '#1d4ed8',
                          boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)'
                        }
                      }}
                    >
                      Save Template
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Fade>
          )}
        </Box>

        {/* RIGHT SIDEBAR - Available Variables (Only visible in Editor mode) */}
        {viewMode === 'editor' && (
          <Box sx={{
            width: { xs: '100%', md: '280px', lg: '300px' },
            flexShrink: 0
          }}>
            <Fade in>
              <Paper elevation={0} sx={{
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                position: { md: 'sticky' },
                top: { md: 24 },
                height: { md: 'calc(100vh - 48px)', xs: 'auto' },
                maxHeight: { md: '2300px', xs: '600px' },
                display: 'flex',
                flexDirection: 'column',
                mb: { xs: 4, md: 0 }
              }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e2e8f0' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>
                    Available Variables
                  </Typography>
                </Box>
                <Box sx={{ p: 2, overflow: 'auto', flexGrow: 1 }}>
                  {AVAILABLE_VARIABLES
                    .filter(group => group.appliesTo.includes(selectedType.id))
                    .map((group, idx, filteredArr) => (
                      <Box key={group.category} sx={{ mb: idx < filteredArr.length - 1 ? 2.5 : 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {group.icon}
                          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#334155' }}>
                            {group.category}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                          {group.variables.map((v) => (
                            <Tooltip key={v} title="Click to copy or drag to editor" placement="left" arrow>
                              <Box
                                draggable
                                onDragStart={(e) => handleDragStart(e, v)}
                                onClick={() => handleCopyVariable(v)}
                                sx={{
                                  px: 1.5, py: 1,
                                  borderRadius: '8px',
                                  border: '1px solid #e2e8f0',
                                  background: '#f8fafc',
                                  cursor: 'grab',
                                  fontSize: '0.78rem',
                                  fontFamily: 'monospace',
                                  color: '#475569',
                                  transition: 'all 0.15s',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  userSelect: 'none',
                                  '&:active': { cursor: 'grabbing' },
                                  '&:hover': {
                                    background: '#f3e8ff',
                                    borderColor: '#d8b4fe',
                                    color: '#8b5cf6',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 2px 4px rgba(139, 92, 246, 0.1)'
                                  }
                                }}
                              >
                                <span>{v}</span>
                                <ContentCopyIcon sx={{ fontSize: 13, opacity: 0.4 }} />
                              </Box>
                            </Tooltip>
                          ))}
                        </Box>
                      </Box>
                    ))}
                </Box>
              </Paper>
            </Fade>
          </Box>
        )}
      </Box>
      <DynamicBasicDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        content={confirmDialog.content}
        confirmButtonText={confirmDialog.confirmButtonText}
      />

      {/* Centered Gmail-Style Split-Panel Modal Preview */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        TransitionComponent={Transition}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            maxWidth: '1400px',
            height: '92vh',
            display: 'flex',
            flexDirection: 'row',
            backgroundColor: 'white',
            boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.18)'
          }
        }}
      >
        {/* Left Section - Email Template Preview (60% width) */}
        <Box sx={{
          flex: 1.5,
          backgroundColor: '#f8fafc',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          borderRight: '1px solid #cbd5e1'
        }}>
          {/* Header */}
          <Box sx={{ px: 4, py: 2.5, borderBottom: '1px solid #cbd5e1', backgroundColor: 'white' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 0.5 }}>
              Email Template Preview
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
              See how your email looks across different themes and devices.
            </Typography>
          </Box>

          {/* Iframe Scroll Area */}
          <Box sx={{
            flexGrow: 1,
            p: 4,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            backgroundColor: '#f8fafc'
          }}>
            {/* Viewport & Email Client Selection */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', mb: 0.4, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  Live Preview
                </Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, backgroundColor: 'white', p: 0.25, borderRadius: '8px', border: '1px solid #cbd5e1', height: 32, boxSizing: 'border-box' }}>
                  {[
                    { id: 'desktop', label: 'Desktop', icon: <DesktopWindowsIcon sx={{ fontSize: 16 }} /> },
                    { id: 'tablet', label: 'Tablet', icon: <TabletIcon sx={{ fontSize: 16 }} /> },
                    { id: 'mobile', label: 'Mobile', icon: <SmartphoneIcon sx={{ fontSize: 16 }} /> }
                  ].map((device) => {
                    const isSelected = previewDevice === device.id;
                    return (
                      <Button
                        key={device.id}
                        variant="text"
                        onClick={() => setPreviewDevice(device.id)}
                        startIcon={device.icon}
                        sx={{
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          height: 26,
                          px: 1.5,
                          color: isSelected ? '#2563eb' : '#64748b',
                          backgroundColor: isSelected ? 'rgba(37,99,235,0.06)' : 'transparent',
                          '&:hover': {
                            backgroundColor: isSelected ? 'rgba(37,99,235,0.08)' : 'rgba(0,0,0,0.02)'
                          }
                        }}
                      >
                        {device.label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>

              <Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#64748b', mb: 0.4, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  Email Client
                </Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, backgroundColor: 'white', p: 0.25, borderRadius: '8px', border: '1px solid #cbd5e1', height: 32, boxSizing: 'border-box' }}>
                  {[
                    { id: 'gmail', label: 'Gmail', icon: <span style={{ color: '#ea4335', fontWeight: 'bold', marginRight: 2, display: 'inline-flex', alignItems: 'center', height: 16, lineHeight: 1 }}>✉</span> },
                    { id: 'outlook', label: 'Outlook', icon: <span style={{ color: '#0078d4', fontWeight: 'bold', marginRight: 2, display: 'inline-flex', alignItems: 'center', height: 16, lineHeight: 1 }}>🟦</span> },
                    { id: 'apple', label: 'Apple Mail', icon: <span style={{ color: '#000', fontWeight: 'bold', marginRight: 2, display: 'inline-flex', alignItems: 'center', height: 16, lineHeight: 1 }}>📬</span> },
                    { id: 'yahoo', label: 'Yahoo Mail', icon: <span style={{ color: '#6001d2', fontWeight: 'bold', marginRight: 2, display: 'inline-flex', alignItems: 'center', height: 16, lineHeight: 1 }}>🟣</span> }
                  ].map((client) => {
                    const isSelected = previewClient === client.id;
                    return (
                      <Button
                        key={client.id}
                        variant="text"
                        onClick={() => setPreviewClient(client.id)}
                        startIcon={client.icon}
                        sx={{
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          height: 26,
                          px: 1.5,
                          color: isSelected ? '#7c3aed' : '#64748b',
                          backgroundColor: isSelected ? 'rgba(124,58,237,0.06)' : 'transparent',
                          '&:hover': {
                            backgroundColor: isSelected ? 'rgba(124,58,237,0.08)' : 'rgba(0,0,0,0.02)'
                          }
                        }}
                      >
                        {client.label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* Envelope Container Mockup */}
            <Box sx={{
              borderRadius: '12px',
              border: '1px solid #cbd5e1',
              overflow: 'hidden',
              backgroundColor: 'white',
              boxShadow: '0 4px 15px -3px rgba(0, 0, 0, 0.05)',
              display: 'flex',
              flexDirection: 'column',
              width: previewDevice === 'desktop' ? '100%' : previewDevice === 'tablet' ? '768px' : '400px',
              maxWidth: '100%',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              alignSelf: 'center'
            }}>
              {/* Conditional Headers by previewClient */}
              {previewClient === 'gmail' && (
                <>
                  {/* Gmail Header */}
                  <Box sx={{
                    backgroundColor: '#f1f3f4',
                    borderBottom: '1px solid #e0e0e0',
                    px: 2, py: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography sx={{ fontWeight: 800, color: '#ea4335', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5, fontFamily: 'sans-serif' }}>
                        <span style={{ fontSize: '1.2rem', color: '#ea4335' }}>✉</span> Gmail
                      </Typography>
                      <Box sx={{
                        backgroundColor: 'white',
                        borderRadius: '24px',
                        px: 2, py: 0.4,
                        display: 'flex',
                        alignItems: 'center',
                        width: { xs: 150, sm: 220, md: 280 },
                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <span style={{ color: '#5f6368', fontSize: '0.8rem', marginRight: 8 }}>🔍</span>
                        <input type="text" placeholder="Search mail" style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.78rem' }} disabled />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#5f6368', fontSize: '1rem' }}>
                      <span>❓</span>
                      <span>⚙️</span>
                      <span>⠿</span>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700 }}>
                        A
                      </Box>
                    </Box>
                  </Box>

                  {/* Gmail subheader */}
                  <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid #f1f3f4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <span style={{ cursor: 'pointer', color: '#5f6368' }}>←</span>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#202124' }}>
                        Interview Invitation for Senior Software Engineer
                      </Typography>
                      <Box sx={{ backgroundColor: '#f1f3f4', color: '#5f6368', fontSize: '0.65rem', px: 1, py: 0.2, borderRadius: '4px', fontWeight: 600 }}>
                        External
                      </Box>
                      <Box sx={{ backgroundColor: '#f1f3f4', color: '#5f6368', fontSize: '0.65rem', px: 1, py: 0.2, borderRadius: '4px', fontWeight: 600 }}>
                        Inbox x
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#5f6368', fontSize: '1rem' }}>
                      <span>☆</span>
                      <span>↩</span>
                      <span>⋮</span>
                    </Box>
                  </Box>

                  {/* Sender details */}
                  <Box sx={{ px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem' }}>
                      A
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#202124' }}>
                          AptiView
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#5f6368' }}>
                          &lt;no-reply@aptiview.ai&gt;
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.72rem', color: '#5f6368' }}>
                        to {liveVariables.candidate_name || 'John Doe'} ▾
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: '#5f6368' }}>
                      10:00 AM (2 minutes ago)
                    </Typography>
                  </Box>
                </>
              )}

              {previewClient === 'outlook' && (
                <>
                  {/* Outlook Header */}
                  <Box sx={{
                    backgroundColor: '#0078d4',
                    color: 'white',
                    px: 2, py: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>🟦</span>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', fontFamily: 'sans-serif' }}>
                        Outlook
                      </Typography>
                      <Box sx={{
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        borderRadius: '4px',
                        px: 2, py: 0.3,
                        display: 'flex',
                        alignItems: 'center',
                        width: { xs: 150, sm: 220, md: 280 }
                      }}>
                        <span style={{ color: 'white', fontSize: '0.8rem', marginRight: 8 }}>🔍</span>
                        <input type="text" placeholder="Search" style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.78rem', background: 'transparent', color: 'white' }} disabled />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: '0.9rem' }}>
                      <span>🔔</span>
                      <span>⚙️</span>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#005a9e', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700 }}>
                        A
                      </Box>
                    </Box>
                  </Box>

                  {/* Outlook Sender details */}
                  <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #f1f3f4', display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#323130' }}>
                      Interview Invitation for Senior Software Engineer
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#0078d4', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem' }}>
                        AV
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#323130' }}>
                            AptiView &lt;no-reply@aptiview.ai&gt;
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#605e5c' }}>
                            Thu 6/25/2026 10:00 AM
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.75rem', color: '#605e5c' }}>
                          To: {liveVariables.candidate_name || 'John Doe'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              {previewClient === 'apple' && (
                <>
                  {/* Apple Mail Header */}
                  <Box sx={{
                    backgroundColor: '#f6f6f6',
                    borderBottom: '1px solid #dcdcdc',
                    px: 3, py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '1.2rem' }}>📬</span>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#4a4a4a', fontFamily: '-apple-system' }}>
                        Inbox — AptiView
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, color: '#6d6d6d' }}>
                      <span>📥</span>
                      <span>📤</span>
                      <span>🗑️</span>
                    </Box>
                  </Box>

                  {/* Apple Mail envelope fields */}
                  <Box sx={{ px: 4, py: 2, borderBottom: '1px solid #e9e9e9', display: 'flex', flexDirection: 'column', gap: 0.6, width: '100%', backgroundColor: '#fafafa' }}>
                    <Box sx={{ display: 'flex', fontSize: '0.8rem' }}>
                      <Typography sx={{ fontWeight: 700, width: 70, color: '#8e8e93' }}>From:</Typography>
                      <Typography sx={{ color: '#1d1d1f', fontWeight: 600 }}>AptiView &lt;no-reply@aptiview.ai&gt;</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', fontSize: '0.8rem' }}>
                      <Typography sx={{ fontWeight: 700, width: 70, color: '#8e8e93' }}>Subject:</Typography>
                      <Typography sx={{ color: '#1d1d1f', fontWeight: 700 }}>Interview Invitation for Senior Software Engineer</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', fontSize: '0.8rem' }}>
                      <Typography sx={{ fontWeight: 700, width: 70, color: '#8e8e93' }}>Date:</Typography>
                      <Typography sx={{ color: '#1d1d1f' }}>June 25, 2026 at 10:00 AM GMT+5:30</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', fontSize: '0.8rem' }}>
                      <Typography sx={{ fontWeight: 700, width: 70, color: '#8e8e93' }}>To:</Typography>
                      <Typography sx={{ color: '#1d1d1f' }}>{liveVariables.candidate_name || 'John Doe'}</Typography>
                    </Box>
                  </Box>
                </>
              )}

              {previewClient === 'yahoo' && (
                <>
                  {/* Yahoo Mail Header */}
                  <Box sx={{
                    backgroundColor: '#6001d2',
                    color: 'white',
                    px: 3, py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', letterSpacing: '-0.5px' }}>
                        yahoo!
                      </Typography>
                      <Box sx={{
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        px: 1.5, py: 0.3,
                        display: 'flex',
                        alignItems: 'center',
                        width: { xs: 150, sm: 220, md: 280 }
                      }}>
                        <input type="text" placeholder="Search mail" style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.78rem' }} disabled />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <span>⚙️</span>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold' }}>
                        Y
                      </Box>
                    </Box>
                  </Box>

                  {/* Yahoo Mail sender area */}
                  <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #e0e4e9', display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#6001d2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.8rem' }}>
                          A
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1d2a3a' }}>
                            AptiView
                          </Typography>
                          <Typography sx={{ fontSize: '0.7' }}>
                            &lt;no-reply@aptiview.ai&gt;
                          </Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: '0.7rem', color: '#5b6b7c' }}>
                        10:00 AM
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#1d2a3a', mt: 1 }}>
                      Interview Invitation for Senior Software Engineer
                    </Typography>
                  </Box>
                </>
              )}

              {/* Safe iframe preview wrapper */}
              <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', p: 3, borderTop: '1px solid #cbd5e1', display: 'flex', justifyContent: 'center', minHeight: '520px' }}>
                <SafeIframePreview html={previewHtml} title="Mail Preview Frame" />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Section - Settings & Controls (40% width) */}
        <Box sx={{
          flex: 1,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
            borderBottom: '1px solid #cbd5e1',
            backgroundColor: 'white'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '1.25rem' }}>⚙️</span>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', fontFamily: "'Poppins', sans-serif" }}>
                Template Settings
              </Typography>
            </Box>
            <IconButton onClick={() => setPreviewDialogOpen(false)} sx={{ color: '#64748b', '&:hover': { backgroundColor: '#f1f5f9', color: '#ef4444' } }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Controls List Scroll Area */}
          <Box sx={{ p: 3, overflowY: 'auto', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3.5 }}>

            {/* 1. Choose Template */}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', mb: 2, fontFamily: "'Poppins', sans-serif" }}>
                1. Choose Template
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1.5,
                overflowX: 'auto',
                pb: 1.5,
                width: '100%',
                '&::-webkit-scrollbar': { height: '5px' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '10px' }
              }}>
                {[
                  {
                    id: 'minimal',
                    label: 'Minimalist Clean',
                    color: '#10b981',
                    renderThumbnail: (brandColor) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: 'white' }}>
                        <Box sx={{ height: 2.5, width: '100%', backgroundColor: brandColor }} />
                        <Box sx={{ p: 0.6, display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 0.4 }}>
                          {/* Header */}
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.1 }}>
                            <Box sx={{ fontSize: '5px', fontWeight: 'bold', color: '#1e293b', scale: '0.8' }}>AptiView</Box>
                          </Box>
                          <Divider sx={{ borderColor: '#f1f5f9', my: 0.1 }} />
                          {/* Content Area */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.2 }}>
                            <Box sx={{ height: 1.5, width: '70%', backgroundColor: '#cbd5e1', borderRadius: 0.2 }} />
                            <Box sx={{ height: 1.5, width: '90%', backgroundColor: '#cbd5e1', borderRadius: 0.2 }} />
                          </Box>
                          {/* CTA Button */}
                          <Box sx={{ alignSelf: 'center', mt: 0.8, height: 6, width: 30, borderRadius: '2px', border: `0.5px solid ${brandColor}`, backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ height: 1, width: 10, backgroundColor: brandColor }} />
                          </Box>
                          {/* Footer */}
                          <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, py: 0.2, borderTop: '0.5px solid #f1f5f9' }}>
                            <Box sx={{ height: 1, width: 40, backgroundColor: '#cbd5e1' }} />
                          </Box>
                        </Box>
                      </Box>
                    )
                  },
                  {
                    id: 'modern_card',
                    label: 'Modern Professional',
                    color: '#2563eb',
                    popular: true,
                    renderThumbnail: (brandColor) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: '#f8fafc', p: 0.4 }}>
                        <Box sx={{ flexGrow: 1, backgroundColor: 'white', borderRadius: '3px', border: '0.5px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                          {/* Header */}
                          <Box sx={{ px: 0.4, py: 0.2, borderBottom: '0.5px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
                              <Box sx={{ width: 2, height: 2, borderRadius: '50%', backgroundColor: brandColor }} />
                              <Box sx={{ fontSize: '4.5px', fontWeight: 800, color: '#0f172a', scale: '0.75' }}>AptiView</Box>
                            </Box>
                            <Box sx={{ height: 1.5, width: 10, backgroundColor: `${brandColor}22`, borderRadius: 0.5 }} />
                          </Box>
                          {/* Content Area */}
                          <Box sx={{ p: 0.4, display: 'flex', flexDirection: 'column', gap: 0.3, flexGrow: 1 }}>
                            <Box sx={{ height: 2.5, width: '60%', backgroundColor: '#0f172a', borderRadius: 0.2 }} />
                            <Box sx={{ height: 1.5, width: '90%', backgroundColor: '#f1f5f9', borderRadius: 0.2 }} />
                            <Box sx={{ height: 1.5, width: '80%', backgroundColor: '#f1f5f9', borderRadius: 0.2 }} />
                            {/* CTA Button */}
                            <Box sx={{ alignSelf: 'center', mt: 0.5, height: 6, width: 32, borderRadius: '1.5px', backgroundColor: brandColor }} />
                          </Box>
                          {/* Footer */}
                          <Box sx={{ p: 0.2, backgroundColor: '#f8fafc', borderTop: '0.5px solid #f1f5f9', display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ height: 1, width: 35, backgroundColor: '#cbd5e1' }} />
                          </Box>
                        </Box>
                      </Box>
                    )
                  },
                  {
                    id: 'formal',
                    label: 'Executive Formal',
                    color: '#0f172a',
                    renderThumbnail: (brandColor) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: '#f1f5f9', p: 0.4 }}>
                        <Box sx={{ flexGrow: 1, backgroundColor: 'white', borderRadius: '2px', borderTop: `2.5px solid ${brandColor}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                          {/* Header */}
                          <Box sx={{ px: 0.4, py: 0.3, borderBottom: '0.5px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.1 }}>
                            <Box sx={{ fontSize: '5px', fontWeight: 'bold', color: '#0f172a', letterSpacing: '0.5px', scale: '0.8' }}>APTIVIEW</Box>
                            <Box sx={{ height: 1, width: 30, backgroundColor: '#cbd5e1' }} />
                          </Box>
                          {/* Content Area */}
                          <Box sx={{ p: 0.4, display: 'flex', flexDirection: 'column', gap: 0.3, flexGrow: 1 }}>
                            <Box sx={{ height: 1.5, width: '90%', backgroundColor: '#475569' }} />
                            <Box sx={{ height: 1.5, width: '85%', backgroundColor: '#475569' }} />
                            <Box sx={{ height: 1.5, width: '90%', backgroundColor: '#475569' }} />
                            <Box sx={{ alignSelf: 'center', mt: 0.4, height: 5, width: 28, border: '0.5px solid #0f172a', borderRadius: '1px' }} />
                          </Box>
                          {/* Footer */}
                          <Box sx={{ p: 0.3, backgroundColor: '#f8fafc', borderTop: '0.5px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                            <Box sx={{ height: 1, width: '90%', backgroundColor: '#cbd5e1' }} />
                            <Box sx={{ height: 1, width: '70%', backgroundColor: '#cbd5e1' }} />
                          </Box>
                        </Box>
                      </Box>
                    )
                  },
                  {
                    id: 'creative',
                    label: 'Enterprise Structured',
                    color: '#f59e0b',
                    renderThumbnail: (brandColor) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: '#f1f5f9', p: 0.4 }}>
                        <Box sx={{ flexGrow: 1, backgroundColor: 'white', borderRadius: '4px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                          {/* Header Banner */}
                          <Box sx={{ backgroundColor: brandColor, py: 0.3, px: 0.4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ fontSize: '5px', fontWeight: '900', color: 'white', scale: '0.8' }}>AptiView</Box>
                            <Box sx={{ height: 1.5, width: 8, backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: 0.5 }} />
                          </Box>
                          {/* Content Area */}
                          <Box sx={{ p: 0.4, display: 'flex', flexDirection: 'column', gap: 0.3, flexGrow: 1 }}>
                            <Box sx={{ height: 1.5, width: '60%', backgroundColor: '#0f172a' }} />
                            {/* Nested Info Card */}
                            <Box sx={{ backgroundColor: '#f8fafc', border: '0.5px solid #e2e8f0', borderRadius: '2px', p: 0.3, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                              <Box sx={{ height: 1, width: '50%', backgroundColor: brandColor }} />
                              <Box sx={{ height: 1, width: '80%', backgroundColor: '#cbd5e1' }} />
                            </Box>
                            {/* CTA button */}
                            <Box sx={{ alignSelf: 'center', mt: 'auto', height: 5, width: 30, backgroundColor: brandColor, borderRadius: '1.5px' }} />
                          </Box>
                          {/* Footer */}
                          <Box sx={{ py: 0.2, px: 0.4, backgroundColor: '#f8fafc', borderTop: '0.5px solid #cbd5e1', display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ height: 1, width: 30, backgroundColor: '#cbd5e1' }} />
                          </Box>
                        </Box>
                      </Box>
                    )
                  },
                  {
                    id: 'compact',
                    label: 'Compact Direct',
                    color: '#ec4899',
                    renderThumbnail: (brandColor) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', backgroundColor: 'white' }}>
                        <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.3, flexGrow: 1 }}>
                          {/* Header */}
                          <Box sx={{ borderBottom: '0.5px solid #f1f5f9', pb: 0.2 }}>
                            <Box sx={{ fontSize: '4.5px', color: '#64748b', fontWeight: 'bold', scale: '0.85', transformOrigin: 'top left' }}>From: AptiView</Box>
                          </Box>
                          {/* Body with vertical left border */}
                          <Box sx={{ borderLeft: `2.5px solid ${brandColor}`, pl: 0.4, display: 'flex', flexDirection: 'column', gap: 0.3, my: 0.1, flexGrow: 1 }}>
                            <Box sx={{ fontSize: '5px', fontWeight: 'bold', color: '#0f172a', scale: '0.8', transformOrigin: 'top left' }}>Hello John,</Box>
                            <Box sx={{ height: 1.5, width: '100%', backgroundColor: '#e2e8f0', borderRadius: 0.2 }} />
                            <Box sx={{ height: 1.5, width: '80%', backgroundColor: '#e2e8f0', borderRadius: 0.2 }} />
                            {/* CTA */}
                            <Box sx={{ height: 5, width: 22, backgroundColor: brandColor, borderRadius: '1.5px', mt: 'auto' }} />
                          </Box>
                          {/* Footer */}
                          <Box sx={{ pt: 0.1, borderTop: '0.5px solid #f1f5f9', display: 'flex' }}>
                            <Box sx={{ height: 1, width: 25, backgroundColor: '#cbd5e1' }} />
                          </Box>
                        </Box>
                      </Box>
                    )
                  }
                ].map((style) => {
                  const isSelected = previewStyleId === style.id;
                  return (
                    <Box
                      key={style.id}
                      onClick={() => handleDialogStyleChange(style.id)}
                      sx={{
                        width: 145,
                        flexShrink: 0,
                        p: 1,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        border: isSelected ? `2.5px solid ${style.color}` : '1.5px solid #e2e8f0',
                        backgroundColor: 'white',
                        transition: 'all 0.2s',
                        position: 'relative',
                        boxShadow: isSelected ? '0 4px 10px rgba(0,0,0,0.05)' : 'none',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          borderColor: isSelected ? style.color : '#cbd5e1'
                        }
                      }}
                    >
                      {isSelected && (
                        <Box sx={{ position: 'absolute', top: 6, right: 6, px: 0.8, py: 0.2, backgroundColor: style.color, color: 'white', fontSize: '0.55rem', fontWeight: 900, borderRadius: '3px', zIndex: 2 }}>
                          ✓ SELECTED
                        </Box>
                      )}
                      {style.popular && !isSelected && (
                        <Box sx={{ position: 'absolute', top: 6, right: 6, px: 0.8, py: 0.2, backgroundColor: 'rgba(37,99,235,0.08)', color: '#2563eb', fontSize: '0.55rem', fontWeight: 900, borderRadius: '3px', zIndex: 2 }}>
                          POPULAR
                        </Box>
                      )}

                      {/* Mini visual mockup box */}
                      <Box sx={{
                        height: 80,
                        border: '1px solid #e2e8f0',
                        backgroundColor: 'white',
                        mb: 1,
                        borderRadius: '6px',
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        {style.renderThumbnail(previewEmailColor)}
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#1e293b' }}>{style.label}</Typography>
                    </Box>
                  );
                })}
              </Box>
              <Typography sx={{ fontSize: '0.72rem', color: '#2563eb', fontWeight: 700, mt: 1.5, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                View All Templates →
              </Typography>
            </Box>

            {/* 2. Brand Color */}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', mb: 2, fontFamily: "'Poppins', sans-serif" }}>
                2. Brand Color
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                {[
                  { name: 'AptiView Purple', hex: '#7c3aed' },
                  { name: 'Corporate Blue', hex: '#2563eb' },
                  { name: 'Emerald Hiring', hex: '#10b981' },
                  { name: 'Teal Accent', hex: '#0d9488' },
                  { name: 'Energetic Orange', hex: '#f97316' },
                  { name: 'Creative Pink', hex: '#f43f5e' },
                  { name: 'Executive Navy', hex: '#0f172a' }
                ].map((color) => {
                  const isSelected = previewEmailColor === color.hex;
                  return (
                    <Tooltip key={color.hex} title={color.name} arrow>
                      <Box
                        onClick={() => handleDialogColorChange('email', color.hex)}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: color.hex,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid transparent',
                          boxShadow: isSelected ? `0 0 0 2.5px white, 0 0 0 4.5px ${color.hex}` : '0 1px 3px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          }
                        }}
                      >
                        {isSelected && <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}>✓</span>}
                      </Box>
                    </Tooltip>
                  );
                })}

                {/* Custom Color Circle */}
                <Tooltip title="Custom Color" arrow>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      border: '1.5px solid #cbd5e1',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        borderColor: '#2563eb'
                      }
                    }}
                  >
                    <span style={{ fontSize: '1rem', color: '#64748b', fontWeight: 'bold' }}>+</span>
                    <input
                      type="color"
                      value={previewEmailColor}
                      onChange={(e) => handleDialogColorChange('email', e.target.value)}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                    />
                  </Box>
                </Tooltip>
              </Box>

              {/* Brand preview area */}
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Brand preview
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: previewEmailColor }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b' }}>
                    {liveVariables.company_name || 'AptiView'}
                  </Typography>
                  <Box sx={{ width: 100, height: 4, borderRadius: 2, backgroundColor: `${previewEmailColor}22` }}>
                    <Box sx={{ width: '60%', height: '100%', borderRadius: 2, backgroundColor: previewEmailColor }} />
                  </Box>
                </Box>
                <Button
                  size="small"
                  onClick={() => handleDialogColorChange('email', '#7c3aed')} // reset to default purple
                  startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#7c3aed',
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': { background: 'transparent', textDecoration: 'underline' }
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Box>

            {/* 3. Company Branding */}
            <Box>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: '#1e293b', mb: 2, fontFamily: "'Poppins', sans-serif" }}>
                3. Company Branding
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Logo upload display */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1.5, border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                    Company Logo
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {previewLogoUrl ? (
                      <Box sx={{ position: 'relative', border: '1px solid #e2e8f0', p: 1, borderRadius: '4px', backgroundColor: '#f8fafc' }}>
                        <img src={previewLogoUrl} alt="Logo Preview" style={{ maxHeight: 32, maxWidth: 120, objectFit: 'contain' }} />
                      </Box>
                    ) : (
                      <Box sx={{
                        width: 48,
                        height: 32,
                        borderRadius: '4px',
                        border: '1.5px dashed #cbd5e1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8fafc',
                        color: '#94a3b8',
                        fontSize: '0.65rem',
                        fontWeight: 700
                      }}>
                        No Logo
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <input
                        accept="image/*"
                        id="logo-upload-file"
                        type="file"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append('file', file);
                            try {
                              const uploadResp = await api.post('/email-templates/upload_media/', formData, {
                                headers: {
                                  'Content-Type': 'multipart/form-data'
                                }
                              });
                              if (uploadResp.data && uploadResp.data.url) {
                                handleDialogLogoChange(uploadResp.data.url);
                              }
                            } catch (err) {
                              console.error('Logo upload failed:', err);
                              toast.error('Failed to upload logo. Please try again.');
                            }
                          }
                        }}
                      />
                      <label htmlFor="logo-upload-file">
                        <Button
                          variant="outlined"
                          component="span"
                          size="small"
                          sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 700, py: 0.5 }}
                        >
                          {previewLogoUrl ? 'Change' : 'Upload'}
                        </Button>
                      </label>
                      {previewLogoUrl && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleDialogLogoChange('')}
                          sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 700, py: 0.5 }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Branding Mode Selector */}
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>
                    Branding Mode
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                    {[
                      { id: 'co_branded', label: 'Co-Branded', subtitle: 'Powered by AptiView' },
                      { id: 'white_label', label: 'White-Label', subtitle: 'Your Brand Only' }
                    ].map((mode) => {
                      const isSelected = previewBrandingMode === mode.id;
                      return (
                        <Button
                          key={mode.id}
                          variant="outlined"
                          onClick={() => handleDialogBrandingModeChange(mode.id)}
                          sx={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 1,
                            px: 1.5,
                            color: isSelected ? '#7c3aed' : '#64748b',
                            borderColor: isSelected ? '#7c3aed' : '#e2e8f0',
                            backgroundColor: isSelected ? 'rgba(124,58,237,0.04)' : 'transparent',
                            borderWidth: isSelected ? '2px' : '1px',
                            '&:hover': {
                              borderColor: isSelected ? '#7c3aed' : '#cbd5e1',
                              backgroundColor: isSelected ? 'rgba(124,58,237,0.08)' : '#f8fafc'
                            }
                          }}
                        >
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{mode.label}</Typography>
                          <Typography sx={{ fontSize: '0.62rem', opacity: 0.8 }}>{mode.subtitle}</Typography>
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                {/* Header Mode Controls */}
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', mb: 1, textTransform: 'uppercase' }}>
                    Header Layout Mode
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                    {[
                      { id: 'logo_and_text', label: 'Logo + Sub' },
                      { id: 'logo_only', label: 'Logo Only' },
                      { id: 'full_banner', label: 'Full Banner' }
                    ].map((mode) => {
                      const isSelected = previewHeaderMode === mode.id;
                      return (
                        <Button
                          key={mode.id}
                          variant="outlined"
                          onClick={() => handleDialogHeaderModeChange(mode.id)}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 0.8,
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            color: isSelected ? '#7c3aed' : '#64748b',
                            borderColor: isSelected ? '#7c3aed' : '#e2e8f0',
                            backgroundColor: isSelected ? 'rgba(124,58,237,0.04)' : 'transparent',
                            borderWidth: isSelected ? '1.5px' : '1px',
                            '&:hover': {
                              borderColor: isSelected ? '#7c3aed' : '#cbd5e1',
                              backgroundColor: isSelected ? 'rgba(124,58,237,0.08)' : '#f8fafc'
                            }
                          }}
                        >
                          {mode.label}
                        </Button>
                      );
                    })}
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Support Email"
                    value={liveVariables.support_email || ''}
                    onChange={(e) => handleDialogVariableChange('support_email', e.target.value)}
                    InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                    inputProps={{ style: { fontSize: '0.8rem' } }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Company Name"
                    value={liveVariables.company_name || ''}
                    onChange={(e) => handleDialogVariableChange('company_name', e.target.value)}
                    InputLabelProps={{ style: { fontSize: '0.75rem' } }}
                    inputProps={{ style: { fontSize: '0.8rem' } }}
                  />
                </Box>
              </Box>
            </Box>

          </Box>

          {/* Action Row Footer */}
          <Box sx={{ p: 2.5, borderTop: '1px solid #cbd5e1', display: 'flex', gap: 1.5, backgroundColor: 'white' }}>
            <Button
              variant="outlined"
              fullWidth
              size="medium"
              startIcon={<SendIcon sx={{ fontSize: 16 }} />}
              onClick={handleSendTestEmail}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', py: 1.2, color: '#475569', borderColor: '#cbd5e1' }}
            >
              Send Test Email
            </Button>
            <Button
              variant="outlined"
              fullWidth
              size="medium"
              startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              onClick={handleCopyPreviewHtml}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', py: 1.2, color: '#475569', borderColor: '#cbd5e1' }}
            >
              Copy HTML
            </Button>
            <Button
              variant="contained"
              fullWidth
              size="medium"
              startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                handleSave();
                setPreviewDialogOpen(false);
              }}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', py: 1.2, backgroundColor: '#7c3aed', color: 'white', '&:hover': { backgroundColor: '#6d28d9' } }}
            >
              Save as Active Template
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Send Test Email Modal */}
      <Dialog
        open={testEmailOpen}
        onClose={() => setTestEmailOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 2.5,
            boxShadow: '0 20px 40px -5px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ p: 0, pb: 2, fontWeight: 800, fontSize: '1.25rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon sx={{ color: '#7c3aed', fontSize: 22 }} /> Send Test Email
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>To:</Typography>
              {userDetails?.email && (
                <Button
                  size="small"
                  onClick={() => setTestEmailTo(userDetails.email)}
                  sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 700, p: 0, minWidth: 'auto', color: '#7c3aed', '&:hover': { background: 'transparent', textDecoration: 'underline' } }}
                >
                  Send to Me
                </Button>
              )}
            </Box>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. candidate@example.com"
              value={testEmailTo}
              onChange={(e) => setTestEmailTo(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              inputProps={{ style: { fontSize: '0.85rem' } }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', mb: 0.8 }}>CC:</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. hr@company.com"
              value={testEmailCc}
              onChange={(e) => setTestEmailCc(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              inputProps={{ style: { fontSize: '0.85rem' } }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', mb: 0.8 }}>Subject:</Typography>
            <TextField
              fullWidth
              size="small"
              value={testEmailSubject}
              onChange={(e) => setTestEmailSubject(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
              inputProps={{ style: { fontSize: '0.85rem' } }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <input
                type="checkbox"
                id="includeChanges"
                checked={testEmailIncludeChanges}
                onChange={(e) => setTestEmailIncludeChanges(e.target.checked)}
                style={{ cursor: 'pointer', width: 16, height: 16, accentColor: '#7c3aed' }}
              />
              <label htmlFor="includeChanges" style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>
                Include current template changes
              </label>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <input
                type="checkbox"
                id="useSampleData"
                checked={testEmailUseSampleData}
                onChange={(e) => setTestEmailUseSampleData(e.target.checked)}
                style={{ cursor: 'pointer', width: 16, height: 16, accentColor: '#7c3aed' }}
              />
              <label htmlFor="useSampleData" style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}>
                Use sample data
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 0, pt: 3.5, gap: 1.5 }}>
          <Button
            onClick={() => setTestEmailOpen(false)}
            variant="outlined"
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', px: 2.5, py: 1, color: '#64748b', borderColor: '#cbd5e1' }}
          >
            Cancel
          </Button>
          <Button
            onClick={executeSendTestEmail}
            variant="contained"
            disabled={sendingTest}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', px: 2.5, py: 1, backgroundColor: '#7c3aed', color: 'white', '&:hover': { backgroundColor: '#6d28d9' } }}
          >
            {sendingTest ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailTemplates;
